import { ApiErrorMessageInternal, TablePagination } from 'noddi-ui-web';
import { CarChangeRow } from './CarChangeRow';
import { DriverChangeReview } from './DriverChangeReview';
import type { NotificationGroupOverviewTranslations } from './notificationTranslations';
import { PickerRow } from './PickerRow';
import { PickerSearch } from './PickerSearch';
import type { MemberCarsFlow } from './useMemberCarsFlow';

type Props = {
  userGroupId: number;
  /** Named in the sentence, not on the button: the button already sits in this member's sheet. */
  memberName: string;
  flow: MemberCarsFlow;
  /** Lets the review clear a silenced-driver warning without leaving the flow. */
  canEditNotifications: boolean;
  translations: NotificationGroupOverviewTranslations;
};

/** The body of the member direction. Its actions live in the sheet footer, with the flow. */
export const MemberCarsPicker = ({ userGroupId, memberName, flow, canEditNotifications, translations: t }: Props) => {
  return (
    <div className='flex flex-col gap-4'>
      {(flow.error || flow.carsError) && <ApiErrorMessageInternal error={[flow.error, flow.carsError]} />}

      {flow.stage === 'review' ? (
        <DriverChangeReview
          userGroupId={userGroupId}
          changes={flow.changes}
          subject='member'
          canEditNotifications={canEditNotifications}
          translations={t}
        />
      ) : (
        <>
          <div className='flex flex-col gap-1'>
            <p className='text-sm text-muted-foreground'>{t.addCarsDescription(memberName)}</p>
            {/* Answers "where are the ones they already drive" without offering a tick that cannot move. */}
            {flow.drivenCount > 0 && (
              <p className='text-sm text-muted-foreground'>{t.alreadyDrivesCount(flow.drivenCount)}</p>
            )}
          </div>

          <PickerSearch value={flow.search} onChange={flow.setSearch} placeholder={t.searchCars} />

          {flow.availableCars.length === 0 && <p className='text-sm text-muted-foreground'>{t.noCarsFound}</p>}

          <div className='flex flex-col gap-1'>
            {flow.availableCars.map((car) => {
              const currentDriver = car.manager ? `${car.manager.firstName} ${car.manager.lastName}`.trim() : undefined;

              return (
                <PickerRow
                  key={car.userGroupCarId}
                  isSelected={flow.pickedCarIds.includes(car.carId)}
                  disabled={flow.isSaving}
                  onClick={() => flow.togglePicked(car)}
                >
                  <CarChangeRow
                    carName={`${car.make} ${car.model}`}
                    licensePlateNumber={car.licensePlate.number}
                    // On every row, picked or not: the driver it comes from is the half of the swap that is easy to miss.
                    note={currentDriver && t.currentDriver(currentDriver)}
                  />
                </PickerRow>
              );
            })}
          </div>
          <TablePagination
            pagination={flow.pagination}
            totalCount={flow.totalCount}
            onChange={(next) => flow.setPagination({ ...next, search: flow.search })}
            loading={flow.isFetching}
            translations={t.pagination}
          />
        </>
      )}
    </div>
  );
};
